<template>
  <div class="p-5 bg-white bg-opacity-50 min-h-screen">
    <div class="bg-white max-w-4xl mx-auto p-6 rounded-xl shadow-md">
      <h1 class="text-2xl font-bold mb-6">
        Controles da Empresa {{ enterpriseName }}
      </h1>

      <div
        v-for="controller in controllers"
        :key="controller._id"
        class="mb-4 flex items-center justify-between"
      >
        <button
          class="w-full text-left bg-blue-100 hover:bg-blue-200 p-4 rounded-md font-semibold"
          @click="openModal(controller._id)"
        >
          {{ controller.ControllerId }} {{ controller.ControllerName }}
          <span
            v-if="wasAccessed(controller._id)"
            class="ml-2 text-green-600 text-xl"
            >✔️</span
          >
        </button>
      </div>
    </div>

    <div
      v-if="showModal"
      class="fixed inset-0 bg-white bg-opacity-50 flex justify-center items-center z-50 ml-70"
    >
      <!-- Contador flutuante -->
      <div
        class="fixed bottom-4 right-4 bg-black text-white px-4 py-2 rounded-full shadow-lg z-50 text-sm"
      >
        {{ answeredQuestions }}/{{ totalQuestions }}
      </div>

      <div
        class="bg-white p-6 rounded-xl w-full max-w-4xl shadow-2xl relative overflow-y-auto max-h-screen"
      >
        <div class="border-b border-gray-300 pb-4 mb-6">
          <h2 class="text-2xl font-bold text-gray-800">
            Auditoria de Controle
          </h2>
          <p class="text-gray-600 mt-2 text-sm">
            Total de perguntas: <strong>{{ selectedQuestions.length }}</strong>
          </p>

          <div class="flex gap-3 items-center text-gray-600 mt-2">
            <span
              class="bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-sm font-semibold"
            >
              {{ selectedController?.ControllerId }}
            </span>
            <span class="text-base">{{
              selectedController?.ControllerName
            }}</span>
          </div>
        </div>

        <div v-for="(q, i) in selectedQuestions" :key="q._id" class="mb-10">
          <div class="mb-6 bg-blue-400 rounded-r-4xl">
            <label class="font-semibold text-gray-700 block mb-2 ml-1.5"
              >Pergunta: {{ i + 1 }}</label
            >
            <div
              class="bg-gray-50 p-4 rounded-md border-l-4 border-blue-400 text-gray-800"
            >
              <p v-for="(questionText, qi) in q.Question" :key="qi">
                {{ questionText }}
              </p>
            </div>
          </div>

          <div class="mb-6 bg-amber-200 rounded-r-4xl">
            <label class="font-semibold text-gray-700 block mb-2 ml-1.5"
              >Objetivo da Pergunta:</label
            >
            <div
              class="bg-gray-50 p-4 rounded-md border-l-4 border-amber-200 text-gray-700"
            >
              {{ q.Option }}
            </div>
          </div>

          <div class="mb-6">
            <label class="font-semibold text-gray-700 block mb-4"
              >Respostas possíveis:</label
            >
            <ul class="space-y-4">
              <li
                v-for="(answer, ai) in q.Answers"
                :key="ai"
                class="p-4 rounded-md border-l-4 shadow-sm transition transform hover:-translate-y-1"
                :class="[
                  ai === 0
                    ? 'border-green-500 bg-green-50'
                    : ai === 1
                      ? 'border-yellow-500 bg-yellow-50'
                      : ai === 2
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-500 bg-gray-100',
                ]"
              >
                <label class="flex items-start font-sans cursor-pointer">
                  <input
                    type="radio"
                    :name="'answer_' + i"
                    :value="answer"
                    class="mr-3 mt-1 scale-125"
                    @change="(e) => logSelectedAnswer(e, i)"
                  />
                  <div class="space-y-1">
                    <div
                      v-for="(sentence, si) in answer
                        .split('.')
                        .filter((s) => s.trim() !== '')"
                      :key="si"
                    >
                      • {{ sentence.trim() }}.
                    </div>
                  </div>
                </label>

                <div v-if="answer.includes('Não se aplica')" class="mt-3">
                  <input
                    type="text"
                    class="w-full p-2 border border-gray-300 rounded-md"
                    placeholder="Justificativa (obrigatória)"
                  />
                  <small class="text-gray-500 text-sm">
                    Exemplo: "A organização está em fase inicial de operação e
                    ainda não formalizou políticas escritas."
                  </small>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div class="mb-6">
          <label class="font-semibold text-gray-700 block mb-2"
            >Anexar Evidências:</label
          >
          <label
            class="block p-6 border-2 border-dashed border-gray-300 rounded-md text-center cursor-pointer hover:bg-blue-50 transition"
          >
            📎 Arraste arquivos ou clique para enviar<br />
            <small class="text-gray-600"
              >Formatos aceitos: PDF, PNG, JPG, DOCX (até 10MB)</small
            >
            <input
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.docx"
              class="hidden"
              ref="fileInput"
              @change="handleFileUpload"
            />
          </label>
        </div>

        <div class="flex justify-end gap-3 mt-8">
          <button
            @click="closeModal"
            class="btn-cancel border border-red-500 text-red-500 px-6 py-2 rounded-md hover:bg-red-50"
          >
            Cancelar
          </button>
          <button
            @click="saveAnswers"
            class="btn-save bg-green-500 text-white px-6 py-2 rounded-md hover:bg-green-600"
          >
            Salvar
          </button>
        </div>
        <button
          @click="closeModal"
          class="absolute top-3 right-4 text-gray-500 hover:text-gray-800 text-2xl"
        >
          ✖
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { getControllersInEmployees } from '@/services/security-controls/api.controllers'
import { getQuestionsByControllerId } from '@/services/qa/api.question'
import { createResponse } from '@/services/qa/api.responses'

export default {
  name: 'QuestionComponent',
  props: {
    enterpriseId: {
      type: String,
      required: true,
    },
    enterpriseName: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      controllers: [],
      accessedControllers: [],
      selectedQuestions: [],
      selectedAnswers: [],
      showModal: false,
      IdEmployee: localStorage.getItem('userId'),
      selectedController: null,
      selectedFile: null,
      totalQuestions: 0,
      answeredQuestions: 0,
    }
  },
  methods: {
    async fetchData() {
      console.log('IdEmployee', this.IdEmployee)
      try {
        const controllerResponse = await getControllersInEmployees(
          this.IdEmployee
        )
        this.controllers = controllerResponse
      } catch (error) {
        console.error('Erro ao buscar controles:', error)
      }
    },
    openModal(controllerId) {
      this.selectedController = this.controllers.find(
        (c) => c._id === controllerId
      )
      this.getQuestionsByController(controllerId)
      this.selectedAnswers = []
      this.showModal = true

      const accessedControllers = JSON.parse(
        localStorage.getItem('accessedControllers') || '[]'
      )
      if (!accessedControllers.includes(controllerId)) {
        accessedControllers.push(controllerId)
        localStorage.setItem(
          'accessedControllers',
          JSON.stringify(accessedControllers)
        )
      }
    },

    async getQuestionsByController(controllerId) {
      try {
        const questions = await getQuestionsByControllerId(controllerId)
        this.selectedQuestions = questions
        this.totalQuestions = questions.length
        this.answeredQuestions = questions.filter(
          (q) => q.selectedAlternative !== undefined
        ).length
      } catch (error) {
        console.error('Erro ao buscar perguntas:', error)
      }
    },

    logSelectedAnswer(event, questionIndex) {
      this.selectedAnswers[questionIndex] = event.target.value
      this.answeredQuestions = this.selectedAnswers.filter(
        (a) => a !== undefined && a !== null
      ).length
      console.log(
        `Pergunta ${questionIndex + 1} - Resposta:`,
        event.target.value
      )
    },

    handleFileUpload(event) {
      const file = event.target.files[0]
      if (file && file.size <= 10 * 1024 * 1024) {
        this.selectedFile = file
      } else {
        alert('Arquivo muito grande ou inválido!')
      }
    },
    async saveAnswers() {
      try {
        const answerLabels = [
          'Implementada',
          'Parcial',
          'Não implementada',
          'Não se aplica',
        ]
        const formattedAnswers = this.selectedAnswers.map(
          (selectedAnswerText, index) => {
            const question = this.selectedQuestions[index]
            const selectedIndex = question.Answers.findIndex(
              (answer) => answer === selectedAnswerText
            )
            return answerLabels[selectedIndex] || 'Não especificado'
          }
        )
        const ensureArray = (val) => {
          if (Array.isArray(val)) return val
          if (val == null || val === '') return []
          return [val]
        }

        const payload = {
          EmpresaId: this.enterpriseId,
          EmployeesId: this.IdEmployee,
          ControllerId: this.selectedController?.ControllerId,
          ControllerName: this.selectedController?.ControllerName,
          TypesOfControls: this.selectedController?.TypesOfControls,
          FunctionalControlTypes: ensureArray(
            this.selectedController?.FunctionalControlTypes
          ),
          PropertyofSI: ensureArray(this.selectedController?.PropertyofSI),
          CybersecurityConcept: ensureArray(
            this.selectedController?.CybersecurityConcept
          ),
          OperationalCapabilities: ensureArray(
            this.selectedController?.OperationalCapabilities
          ),
          DomainsDeSI: ensureArray(this.selectedController?.DomainsDeSI),
          QuestionId: this.selectedQuestions.map((q) => q._id),
          Answers: formattedAnswers,
        }

        await createResponse(payload)

        alert('Respostas e arquivo enviados com sucesso!')
        this.closeModal()
      } catch (error) {
        console.error('Erro ao salvar respostas:', error)
        alert('Erro ao salvar respostas. Veja o console.')
      }
    },
    closeModal() {
      this.showModal = false
      this.selectedQuestions = []
      this.selectedAnswers = []
    },

    wasAccessed(controllerId) {
      const accessed = JSON.parse(
        localStorage.getItem('accessedControllers') || '[]'
      )
      return accessed.includes(controllerId)
    },
  },
  mounted() {
    this.fetchData()
  },
}
</script>
